import React, { useContext, useEffect, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./ClienteMaquinas.css";
import { Button, Col, Row } from "antd";
import axios from "axios";
import * as links from "../../../../utils/links";
import { AuthContext } from "../../../../contexts/AuthContext";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import LoadingAction from "../../../../themes/LoadingAction/LoadingAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faCheckCircle, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { AiOutlineEdit, AiOutlinePlusCircle, AiOutlineQrcode } from "react-icons/ai";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const moeda = (valor) => Number(valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const normalizarDescricaoMaquina = (descricao) => {
  const texto = String(descricao || "").trim();
  if (!texto) return texto;
  return texto.replace(/^m[aá]quina\b/i, "Máquina");
};

const numeroDescricaoMaquina = (descricao) => {
  const match = String(descricao || "").match(/(\d+)/);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
};

const ordenarMaquinasPorDescricao = (lista) =>
  [...(Array.isArray(lista) ? lista : [])]
    .map((item) => ({ ...item, descricao: normalizarDescricaoMaquina(item?.descricao) }))
    .sort((a, b) => {
      const numeroA = numeroDescricaoMaquina(a?.descricao);
      const numeroB = numeroDescricaoMaquina(b?.descricao);
      if (numeroA !== numeroB) return numeroA - numeroB;
      return String(a?.descricao || "").localeCompare(String(b?.descricao || ""), "pt-BR", {
        numeric: true,
        sensitivity: "base",
      });
    });

const statusVisualMaquina = (post) => {
  const statusServidor = String(post?.status || "").toUpperCase();

  if (statusServidor === "OFFLINE") return "OFFLINE";

  const ultimoPagamento = post?.ultimoPagamentoRecebido
    ? new Date(post.ultimoPagamentoRecebido).getTime()
    : NaN;
  const idadePagamento = Date.now() - ultimoPagamento;

  if (
    Number.isFinite(ultimoPagamento) &&
    idadePagamento >= 0 &&
    idadePagamento < 15 * 60 * 1000
  ) {
    return "PAGAMENTO_RECENTE";
  }

  if (statusServidor === "PAGAMENTO_RECENTE") return "PAGAMENTO_RECENTE";
  return "ONLINE";
};

const ClienteMaquinas = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const { dataUser } = authInfo;
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const token = dataUser?.token;

  const [clienteAtual, setClienteAtual] = useState(location.state || null);
  const [totalClienteMaquinas, setTotalClienteMaquinas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [financeiro, setFinanceiro] = useState({ pixHoje: 0, pixMes: 0, periodoHoje: "00:00 às 23:59", periodoMes: "dia 01 ao último dia do mês", qtdHoje: 0, qtdMes: 0 });

  const headers = { "x-access-token": token, "content-type": "application/json" };

  const tratarSessao = (err) => {
    if ([401, 403].includes(err?.response?.status)) {
      setNotiMessage({ type: "error", message: "A sua sessão expirou, para continuar faça login novamente." });
      navigate(dataUser?.key === "ADMIN" ? links.ADMIN_SIGNIN : links.SIGNIN);
      setDataUser(null);
      return true;
    }
    return false;
  };

  const carregarCliente = async () => {
    if (clienteAtual?.id === id) return clienteAtual;
    try {
      const res = await axios.get(`${process.env.REACT_APP_SERVIDOR}/admin-2027/clientes?incluirArquivados=1`, { headers });
      const encontrado = (Array.isArray(res.data) ? res.data : []).find((c) => String(c.id) === String(id));
      if (encontrado) setClienteAtual(encontrado);
      return encontrado || clienteAtual;
    } catch (_) {
      return clienteAtual;
    }
  };

  const carregarFinanceiro = async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVIDOR}/admin-2026/clientes/${encodeURIComponent(id)}/visao-geral`, { headers });
    const d = res.data || {};
    const recentes = Array.isArray(d.pagamentosRecentes) ? d.pagamentosRecentes : [];
    const hojeTexto = String(d.periodoHoje || "").slice(-10);
    const qtdHoje = recentes.filter((p) => String(p.data || "").slice(0, 10) === hojeTexto.split("/").reverse().join("-")).length;
    setFinanceiro({
      pixHoje: Number(d.pixHoje || 0),
      pixMes: Number(d.pixMes || 0),
      periodoHoje: d.periodoHoje || "00:00 às 23:59",
      periodoMes: d.periodoMes || "dia 01 ao último dia do mês",
      qtdHoje,
      qtdMes: recentes.length,
    });
  };

  const dataData = async () => {
    setIsLoading(true);
    try {
      await carregarCliente();
      const maquinasRes = await axios.get(`${process.env.REACT_APP_SERVIDOR}/maquinas-adm?id=${id}`, { headers });
      setTotalClienteMaquinas(ordenarMaquinasPorDescricao(maquinasRes.data));
      try {
        await carregarFinanceiro();
      } catch (financeErr) {
        if (!tratarSessao(financeErr)) setFinanceiro((prev) => ({ ...prev, pixHoje: 0, pixMes: 0 }));
      }
    } catch (err) {
      if (!tratarSessao(err)) setNotiMessage({ type: "error", message: "Não foi possível atualizar os dados desta empresa agora." });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dataData();
    const intervalId = setInterval(dataData, 60000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  useEffect(() => {
    if (!totalClienteMaquinas?.length) return;
    const ultimaId = sessionStorage.getItem("ultimaMaquinaAberta");
    if (!ultimaId) return;
    let tentativas = 0;
    const restaurar = () => {
      const el = document.getElementById(`maquina-card-${ultimaId}`);
      if (!el) return false;
      el.scrollIntoView({ behavior: "auto", block: "center" });
      setTimeout(() => el.scrollIntoView({ behavior: "auto", block: "center" }), 500);
      sessionStorage.removeItem("ultimaMaquinaAberta");
      return true;
    };
    const timer = setInterval(() => {
      tentativas += 1;
      if (restaurar() || tentativas >= 30) clearInterval(timer);
    }, 300);
    return () => clearInterval(timer);
  }, [totalClienteMaquinas]);

  const sinalIcone = (post) => {
    if (statusVisualMaquina(post) === "OFFLINE" || !post.nivelDeSinal) return <i className="bi bi-reception-0 maquina-sinal maquina-sinal-vazio" />;
    const nivel = Number(post.nivelDeSinal || 0);
    const classe = nivel <= 30 ? "bi-reception-1" : nivel <= 60 ? "bi-reception-2" : nivel <= 70 ? "bi-reception-3" : "bi-reception-4";
    return (
      <OverlayTrigger key={post.id} placement="top" overlay={<Tooltip id={`tooltip-top-${post.id}`}>{nivel}%</Tooltip>}>
        <i className={`bi ${classe} maquina-sinal`} />
      </OverlayTrigger>
    );
  };

  const clienteState = clienteAtual || location.state || { id, nome: "Empresa" };

  return (
    <div className="Cliente_Maquinas_container">
      {isLoading && <LoadingAction />}
      <div className="WarningMsgSpan"><span>{dataUser?.warningMsg}</span></div>

      <div className="AddCliente_header">
        <div className="AddCliente_header_title">Dispositivos do cliente:</div>
        <button className="AddCliente_header_back" type="button" onClick={() => navigate(links.DASHBOARD_CLIENTES)}>VOLTAR</button>
      </div>

      <div className="Cliente_Maquinas_header">
        <div className="Cliente_Maquinas_staBlockTitle">{clienteState.nome || "Empresa"}</div>
      </div>

      <div className="empresa-pix-resumo">
        <div className="empresa-pix-card">
          <span>PIX DE HOJE</span>
          <strong>{moeda(financeiro.pixHoje)}</strong>
          <small>{financeiro.periodoHoje} • todas as máquinas desta empresa</small>
        </div>
        <div className="empresa-pix-card destaque">
          <span>PIX DO MÊS</span>
          <strong>{moeda(financeiro.pixMes)}</strong>
          <small>{financeiro.periodoMes} • todas as máquinas desta empresa</small>
        </div>
      </div>

      <div className="empresa-pix-explicacao">
        O diário soma apenas os PIX desta empresa entre 00:00 e 23:59. O mensal soma do dia 01 até o último dia do mês e zera automaticamente no início do próximo mês.
      </div>

      <div className="Cliente_Maquinas_action">
        <Button className="Cliente_Maquinas_refreshbtn" onClick={dataData}><FontAwesomeIcon icon={faArrowsRotate} /><span>Atualizar</span></Button>
        <Button className="Cliente_Maquinas_addbtn" onClick={() => navigate(`${links.ADD_CLIENTES_MAQUINA_ADM}/${id}`, { state: clienteState })}><AiOutlinePlusCircle /><span>Adicionar Máquina</span></Button>
        <Button className="Cliente_Maquinas_editbtn" onClick={() => navigate(`${links.EDITAR_CLIENTES}/${id}`, { state: clienteState })}><AiOutlineEdit /><span>Editar Cliente</span></Button>
        <Button className="Cliente_Maquinas_editbtn" onClick={() => navigate(`${links.CLIENTE_MENSALIDADES_ADM}/${id}`, { state: clienteState })}><AiOutlineQrcode /><span>Mensalidades</span></Button>
      </div>

      <Row>
        {totalClienteMaquinas.map((post) => {
          const statusVisual = statusVisualMaquina(post);
          return (
            <Col key={post.id} id={`maquina-card-${post.id}`} xs={24} md={24} lg={8} xl={8} className="Cliente_Maquinas_col">
              <div onClick={() => {
                sessionStorage.setItem("ultimaMaquinaAberta", String(post.id));
                navigate(`${links.CLIENTES_MAQUINAS_FORNECEDOR_SEARCH}/${post.id}`, { state: { clienteInfo: clienteState, maquinaInfos: post } });
              }}>
                <div className="maquina">
                  <div className="maquina-info">
                    <div className="maquina-status-topo">
                      <FontAwesomeIcon icon={statusVisual === "OFFLINE" ? faXmarkCircle : faCheckCircle} className={`logout-icon fa-3x ${statusVisual === "OFFLINE" ? "status-offline" : statusVisual === "PAGAMENTO_RECENTE" ? "status-pagamento" : "status-online"}`} />
                      {sinalIcone(post)}
                    </div>
                    <h2>{post.nome}</h2>
                    <h6>{statusVisual} - {post.descricao}</h6>
                    <h6>MP: {post.store_id}</h6>
                    <h6>PAG: {post.maquininha_serial}</h6>
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default ClienteMaquinas;
